<template>
  <div class="min-h-screen bg-gradient-to-br from-psychology-calm via-white to-psychology-warm">
    <!-- 顶部导航 -->
    <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200 no-print">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <router-link to="/" class="inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              </svg>
              首页
            </router-link>
            <div class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-psychology-primary">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
              <span class="font-semibold text-psychology-primary">RPI 评估结果</span>
            </div>
          </div>
          <div class="flex gap-2">
            <!-- 下载按钮（带下拉菜单） -->
            <div class="relative">
              <button 
                @click="toggleDownloadMenu"
                :disabled="isExporting"
                class="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" x2="12" y1="15" y2="3"></line>
                </svg>
                <span class="hidden sm:inline">{{ isExporting ? '导出中...' : '下载' }}</span>
              </button>
              
              <!-- 下拉菜单 -->
              <div 
                v-if="showDownloadMenu" 
                class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10"
                @click.stop
              >
                <button 
                  @click="handleDownloadPDF"
                  class="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" x2="8" y1="13" y2="13"></line>
                    <line x1="16" x2="8" y1="17" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  下载为 PDF
                </button>
                <button 
                  @click="handleDownloadImage"
                  class="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                    <circle cx="9" cy="9" r="2"></circle>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                  </svg>
                  下载为图片
                </button>
                <button 
                  @click="handlePrint"
                  class="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect width="12" height="8" x="6" y="14"></rect>
                  </svg>
                  打印报告
                </button>
                <hr class="my-1">
                <button 
                  @click="handleDownloadJSON"
                  class="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm text-gray-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                    <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <path d="m3 15 2 2 4-4"></path>
                  </svg>
                  导出数据 (JSON)
                </button>
              </div>
            </div>

            <button @click="handleShare" class="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" x2="15.42" y1="13.51" y2="17.49"></line>
                <line x1="15.41" x2="8.59" y1="6.51" y2="10.49"></line>
              </svg>
              <span class="hidden sm:inline">分享结果</span>
            </button>
            <button @click="handleRetake" class="hidden sm:inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                <path d="M8 16H3v5"></path>
              </svg>
              重新测评
            </button>
            <button @click="handleRetake" class="sm:hidden inline-flex items-center justify-center p-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                <path d="M8 16H3v5"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main v-if="result" ref="resultContent" class="container mx-auto px-4 py-8 space-y-8">
      <!-- RPI分数卡片 -->
      <RPIScoreCard 
        :rpi-score="result.rpiScore"
        :rpi-level="result.rpiLevel"
        :recommendations="recommendations"
        :interpretation="result.interpretation"
        :assessment-type="result.assessmentType"
      />

      <!-- 四维度分析 -->
      <DimensionAnalysis 
        :dimension-results="result.dimensionResults"
      />

      <!-- 四维度雷达图 -->
      <DimensionRadarChart 
        v-if="false"
        :dimension-results="result.dimensionResults"
      />

      <!-- 人格类型 -->
      <PersonalityType 
        :personality-type="result.personalityType"
        :rpi-score="result.rpiScore"
        :assessment-type="result.assessmentType"
      />

      <!-- 依恋类型 -->
      <AttachmentType 
        :attachment-type="result.attachmentType"
        :assessment-type="result.assessmentType"
      />

      <!-- 量表详细分数 -->
      <DetailedScores 
        :dimension-results="result.dimensionResults"
      />

      <!-- 爱情心理学专家建议 -->
      <ExpertRecommendations 
        :recommendations="expertRecommendations"
      />

      <!-- 健康关系养成指南 -->
      <HealthyRelationshipGuide />

      <!-- 星座性格参考 -->
      <ZodiacReference 
        v-if="zodiacSign && zodiacSign > 0"
        :zodiac-sign="zodiacSign"
        :assessment-type="result.assessmentType"
      />

      <!-- 评估信息 -->
      <AssessmentInfo 
        :assessment-type="result.assessmentType"
        :end-time="assessmentInfo.endTime"
        :responses-count="assessmentInfo.responsesCount"
        :session-id="assessmentInfo.sessionId"
      />

      <!-- 重要声明 -->
      <div class="card border-yellow-200 bg-yellow-50/50">
        <div class="p-6">
          <div class="flex items-start gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" x2="12" y1="8" y2="12"></line>
              <line x1="12" x2="12.01" y1="16" y2="16"></line>
            </svg>
            <div class="space-y-2 text-sm">
              <h4 class="font-semibold text-yellow-800">重要声明</h4>
              <p class="text-yellow-700 leading-relaxed">
                本测评结果仅供参考，不构成专业诊断。RPI指数是基于心理学研究的测评工具，
                旨在帮助{{ result.assessmentType === 'self' ? '你' : '你' }}了解{{ result.assessmentType === 'self' ? '自己' : '恋人' }}在恋爱关系中的占有欲特点。如果{{ result.assessmentType === 'self' ? '你' : '你' }}对结果有疑问或遇到严重关系问题，
                可以考虑咨询专业的心理咨询师或情感咨询师。
              </p>
              <p class="text-yellow-700 leading-relaxed">
                {{ result.assessmentType === 'self' ? '您的' : '您的' }}所有数据都安全地保存在本地设备上，我们不会收集或传输{{ result.assessmentType === 'self' ? '您的' : '您的' }}个人信息。
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部按钮 -->
      <div class="flex flex-wrap justify-center gap-3 pt-6 no-print">
        <router-link to="/" class="btn-secondary">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
            <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          </svg>
          返回首页
        </router-link>
        <button 
          @click="handleDownloadPDF" 
          :disabled="isExporting"
          class="bg-psychology-primary hover:bg-psychology-primary/90 text-white px-4 py-2 rounded-md inline-flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
          </svg>
          <span>{{ isExporting ? '生成中...' : '下载PDF' }}</span>
        </button>
        <button @click="handleRetake" class="btn-secondary">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
            <path d="M21 3v5h-5"></path>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
            <path d="M8 16H3v5"></path>
          </svg>
          重新测评
        </button>
        <button @click="handleShare" class="bg-psychology-accent hover:bg-psychology-accent/90 text-white px-4 py-2 rounded-md inline-flex items-center gap-2 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <circle cx="18" cy="5" r="3"></circle>
            <circle cx="6" cy="12" r="3"></circle>
            <circle cx="18" cy="19" r="3"></circle>
            <line x1="8.59" x2="15.42" y1="13.51" y2="17.49"></line>
            <line x1="15.41" x2="8.59" y1="6.51" y2="10.49"></line>
          </svg>
          <span class="hidden sm:inline">分享结果</span>
        </button>
      </div>
    </main>

    <!-- 错误状态 -->
    <div v-else-if="errorMessage" class="container mx-auto px-4 py-20">
      <div class="max-w-2xl mx-auto text-center">
        <div class="card p-8">
          <div class="space-y-4">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8 text-red-600">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" x2="9" y1="9" y2="15"></line>
                <line x1="9" x2="15" y1="9" y2="15"></line>
              </svg>
            </div>
            <h2 class="text-xl font-semibold text-red-600 mb-2">加载失败</h2>
            <p class="text-gray-600 mb-6">{{ errorMessage }}</p>
            <div class="space-y-3">
              <router-link to="/" class="btn-primary">
                返回首页
              </router-link>
              <button @click="loadSessionData" class="btn-secondary">
                重新加载
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载中 -->
    <div v-else class="container mx-auto px-4 py-20 text-center">
      <div class="text-gray-500">正在加载结果...</div>
    </div>

    <!-- 回到顶部 -->
    <BackToTop />

    <!-- 分享对话框 -->
    <ShareDialog 
      v-if="result"
      :is-open="showShareDialog"
      :rpi-score="result.rpiScore"
      :rpi-level="result.rpiLevel"
      @close="showShareDialog = false"
    />
  </div>
</template>

<script>
import { getSessionById, migrateOldData } from '../utils/sessionStorage.js'
import { exportToPDF, exportToImage, exportToJSON, printResult } from '../utils/exportUtils.js'
import RPIScoreCard from '../components/result/RPIScoreCard.vue'
import DimensionAnalysis from '../components/result/DimensionAnalysis.vue'
import DimensionRadarChart from '../components/result/DimensionRadarChart.vue'
import PersonalityType from '../components/result/PersonalityType.vue'
import AttachmentType from '../components/result/AttachmentType.vue'
import DetailedScores from '../components/result/DetailedScores.vue'
import ExpertRecommendations from '../components/result/ExpertRecommendations.vue'
import HealthyRelationshipGuide from '../components/result/HealthyRelationshipGuide.vue'
import ZodiacReference from '../components/result/ZodiacReference.vue'
import AssessmentInfo from '../components/result/AssessmentInfo.vue'
import BackToTop from '../components/BackToTop.vue'
import ShareDialog from '../components/result/ShareDialog.vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'ResultPage',
  components: {
    RPIScoreCard,
    DimensionAnalysis,
    DimensionRadarChart,
    PersonalityType,
    AttachmentType,
    DetailedScores,
    ExpertRecommendations,
    HealthyRelationshipGuide,
    ZodiacReference,
    AssessmentInfo,
    BackToTop,
    ShareDialog
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      result: null,
      recommendations: [],
      expertRecommendations: [],
      zodiacSign: null,
      showShareDialog: false,
      showDownloadMenu: false,
      isExporting: false,
      assessmentInfo: {
        endTime: '',
        responsesCount: 0,
        sessionId: ''
      },
      session: null,
      errorMessage: ''
    }
  },
  mounted() {
    // 点击外部关闭下拉菜单
    document.addEventListener('click', this.closeDownloadMenu)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeDownloadMenu)
  },
  created() {
    this.loadSessionData()
  },
  methods: {
    loadSessionData() {
      try {
        // 迁移旧数据（如果存在）
        migrateOldData()
        
        // 获取sessionId
        const sessionId = this.$route.query.sessionId || this.$route.query.session || this.$route.query.id
        
        if (!sessionId) {
          this.errorMessage = '未找到会话ID。请确保从正确的链接访问结果页面。'
          return
        }
        
        // 根据sessionId获取会话
        const session = getSessionById(sessionId)
        
        if (!session) {
          this.errorMessage = `未找到会话ID为 "${sessionId}" 的评估记录。可能已被删除或损坏。`
          return
        }
        
        if (!session.completed || !session.results) {
          this.errorMessage = '该评估尚未完成，无法查看结果。请先完成评估。'
          return
        }
        
        // 设置会话数据
        this.session = session
        this.result = session.results
        
        // 确保所有维度都有tScore字段
        this.ensureTScoreExists()
        
        // 设置建议数据
        this.recommendations = this.result.recommendations || []
        this.expertRecommendations = this.result.expertRecommendations || []
        
        // 设置星座信息（兼容旧字段zodiac与新字段zodiacSign）
        const zodiacFromSession = session.demographics?.zodiac ?? session.demographics?.zodiacSign ?? 0
        this.zodiacSign = Number(zodiacFromSession) || 0
        
        // 设置评估信息
        this.assessmentInfo = {
          endTime: session.endTime || this.result.endTime || new Date().toISOString(),
          responsesCount: this.result.totalQuestions || 60,
          sessionId: session.id
        }
        
        // 清理进度提示，避免"未完成"提示
        localStorage.setItem('rpi_assessment_completed', 'true')
        localStorage.removeItem('rpi_assessment_progress')
        
        console.log('会话数据加载成功:', {
          sessionId: session.id,
          type: session.type,
          completed: session.completed,
          hasResults: !!session.results
        })
        
      } catch (error) {
        console.error('加载会话数据失败:', error)
        this.errorMessage = '加载评估结果时发生错误，请重试。'
      }
    },
    ensureTScoreExists() {
      // 确保所有维度都有tScore字段
      if (this.result && this.result.dimensionResults) {
        this.result.dimensionResults.forEach(dimension => {
          if (typeof dimension.tScore === 'undefined' && typeof dimension.zScore === 'number') {
            // 重新计算T分数
            dimension.tScore = 50 + 10 * dimension.zScore
          }
        })
      }
    },
    toggleDownloadMenu() {
      this.showDownloadMenu = !this.showDownloadMenu
    },
    closeDownloadMenu() {
      this.showDownloadMenu = false
    },
    async handleDownloadPDF() {
      this.showDownloadMenu = false
      this.isExporting = true
      
      try {
        const assessmentType = this.result.assessmentType === 'self' ? '自测' : '恋人测'
        const filename = `RPI评估结果_${assessmentType}_${new Date().toISOString().split('T')[0]}.pdf`
        
        await exportToPDF(this.$refs.resultContent, filename)
        
        this.toast.success('PDF下载成功！')
      } catch (error) {
        console.error('PDF下载失败:', error)
        this.toast.error('PDF生成失败，请稍后重试')
      } finally {
        this.isExporting = false
      }
    },
    async handleDownloadImage() {
      this.showDownloadMenu = false
      this.isExporting = true
      
      try {
        const assessmentType = this.result.assessmentType === 'self' ? '自测' : '恋人测'
        const filename = `RPI评估结果_${assessmentType}_${new Date().toISOString().split('T')[0]}.png`
        
        await exportToImage(this.$refs.resultContent, filename)
        
        this.toast.success('图片下载成功！')
      } catch (error) {
        console.error('图片下载失败:', error)
        this.toast.error('图片生成失败，请稍后重试')
      } finally {
        this.isExporting = false
      }
    },
    handleDownloadJSON() {
      this.showDownloadMenu = false
      
      try {
        const assessmentType = this.result.assessmentType === 'self' ? '自测' : '恋人测'
        const filename = `RPI评估数据_${assessmentType}_${new Date().toISOString().split('T')[0]}.json`
        
        exportToJSON(this.result, filename)
        
        this.toast.success('数据导出成功！')
      } catch (error) {
        console.error('数据导出失败:', error)
        this.toast.error('数据导出失败，请稍后重试')
      }
    },
    handlePrint() {
      this.showDownloadMenu = false
      
      try {
        printResult()
        this.toast.info('正在准备打印...')
      } catch (error) {
        console.error('打印失败:', error)
        this.toast.error('打印失败，请稍后重试')
      }
    },
    handleShare() {
      // 优先使用原生分享API（移动端）
      if (navigator.share) {
        navigator.share({
          title: `${this.result.assessmentType === 'self' ? '我的' : 'Ta的'}恋爱占有欲指数测评结果`,
          text: `${this.result.assessmentType === 'self' ? '我的' : 'Ta的'}RPI指数是${this.result.rpiScore}，${this.result.rpiLevel.level}！`,
          url: window.location.href
        }).catch(err => {
          console.log('分享失败:', err)
          // 降级到对话框
          this.showShareDialog = true
        })
      } else {
        // 桌面端显示分享对话框
        this.showShareDialog = true
      }
    },
    handleRetake() {
      // 清除之前的数据
      const assessmentType = this.$route.query.type || 'self'
      const storageKey = `rpi_answers_${assessmentType}`
      localStorage.removeItem(storageKey)
      localStorage.removeItem('rpi_answers') // 清理可能存在的旧数据
      localStorage.removeItem('rpi_result')
      localStorage.removeItem('rpi_basic_info')
      localStorage.removeItem('rpi_consent')
      localStorage.removeItem('rpi_assessment_progress')
      
      // 返回首页
      this.$router.push('/')
    }
  }
}
</script>

